import { from, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ReportStore, reportStore } from './report.store';
import {
  Properties,
  Reservations,
  PointOfSale,
  PaginationState,
  exportFileName,
  dispatchCancel,
  dispatchForm,
  CommonForm,
  MediaTypes,
} from '@lib/state';
import { format, lightFormat } from 'date-fns';
import { applyTransaction, setLoading } from '@datorama/akita';
import {
  appendData,
  InvoiceSummaryRequest,
  ReportType,
  UnbilledEarnedReportRequest,
} from './report.model';
import { EnumValueHumanizer } from '@lib/common';
import { ReportForms } from './report.forms';

export class ReportService {
  constructor(
    private readonly store: ReportStore,
    private readonly propertiesReportApi: Properties.ReportApi,
    private readonly reservationReportApi: Reservations.ReportApi,
    private readonly ledgerApi: Reservations.LedgerApi,
    private readonly posReportApi: PointOfSale.ReportApi
  ) {}

  private queryLimit = 10;

  loadInHouseGuests(propertyId: string, date: Date, corporateAccountName?: string): void {
    const d = date.toISOString();
    from(
      this.propertiesReportApi.reportPropertyIdInHouseGet(
        propertyId,
        d,
        corporateAccountName || undefined
      )
    )
      .pipe(
        setLoading(this.store),
        map(response => response.data.data),
        dispatchCancel(this.store)
      )
      .subscribe(data => {
        this.store.update({
          report: {
            type: 'InHouseGuestReport',
            data: data,
          },
        });
      });
  }

  loadRoomOccupancy(propertyId: string, start: Date, end: Date, excludeNotCharge: boolean): void {
    let startDate = lightFormat(start, 'yyyy-MM-dd');
    let endDate = lightFormat(end, 'yyyy-MM-dd');

    from(
      this.reservationReportApi.reportOccupancyGet(propertyId, startDate, endDate, excludeNotCharge)
    )
      .pipe(
        setLoading(this.store),
        map(response => response.data.data),
        dispatchCancel(this.store)
      )
      .subscribe(data => {
        this.store.update({
          report: {
            type: 'OccupancyReport',
            data: data,
          },
        });
      });
  }

  loadPurchaseTransactions(
    propertyId: string,
    start: Date,
    end: Date,
    purchaseType?: PointOfSale.PurchaseType,
    continuationToken?: string | null,
    limit?: number
  ): void {
    let startDate = lightFormat(start, 'yyyy-MM-dd');
    let endDate = lightFormat(end, 'yyyy-MM-dd');

    if (!continuationToken) this.store.reset();

    from(
      this.posReportApi.reportPropertyIdPurchasesGet(
        propertyId,
        continuationToken ?? undefined,
        limit ?? this.queryLimit,
        startDate,
        endDate,
        purchaseType
      )
    )
      .pipe(
        setLoading(this.store),
        map(response => response.data),
        dispatchCancel(this.store)
      )
      .subscribe(({ data, ...pagination }) => {
        applyTransaction(() => {
          this.store.update(x => ({
            report: appendData(x.report, 'PurchaseTransactionReport', data) as ReportType,
          }));
          this.updatePaginationState(pagination);
        });
      });
  }

  exportTransactions(
    propertyId: string,
    startDate: Date,
    endDate: Date,
    purchaseType?: PointOfSale.PurchaseType
  ) {
    from(
      this.posReportApi.reportPropertyIdPurchasesExportGet(
        propertyId,
        lightFormat(startDate, 'yyyy-MM-dd'),
        lightFormat(endDate, 'yyyy-MM-dd'),
        purchaseType
      )
    )
      .pipe(
        map(x => new Blob([x.data], { type: 'text/csv' })),
        dispatchForm(CommonForm.Export)
      )
      .subscribe(x => {
        try {
          // Perform the asynchronous saveAs operation
          saveAs(x, exportFileName(startDate, endDate));
        } catch (error) {
          // Handle the error
          console.error('Error saving file:', error);
        }
      });
  }

  exportUnbilledReservations(
    propertyId: string,
    startDate: Date,
    endDate: Date,
    recipients: string
  ) {
    from(
      this.ledgerApi.ledgerTransactionsNotInvoicedReportPost({
        recipients,
        propertyId,
        start: lightFormat(startDate, 'yyyy-MM-dd'),
        end: lightFormat(endDate, 'yyyy-MM-dd'),
      })
    )
      .pipe(dispatchForm(ReportForms.UnbilledTransactions))
      .subscribe();
  }

  private updatePaginationState({ isDone, continuationToken }: PaginationState) {
    this.store.update(() => ({
      pagination: { isDone, continuationToken },
    }));
  }

  exportTaxCreditReport(
    propertyId: string,
    startDate: Date,
    endDate: Date,
    excludeRetroactiveTax: boolean
  ) {
    from(
      this.reservationReportApi.reportTaxesEmailReportGet(
        propertyId,
        lightFormat(startDate, 'yyyy-MM-dd'),
        lightFormat(endDate, 'yyyy-MM-dd'),
        excludeRetroactiveTax
      )
    )
      .pipe(dispatchForm(CommonForm.Export))
      .subscribe();
  }

  // Currently has no caller: the Combined Occupancy Report action was removed from the
  // Occupancy page while its range mode (From != To) reported zeros for stay dates the daily
  // housing snapshot never covered. The export now reads live data and never the snapshot, but a
  // range request still summarizes only its first date (spec 005). Retained deliberately so
  // spec 005 can re-wire the UI action without rebuilding this binding. Do not delete as dead code.
  exportDailyOccupancyReport(
    startDate: Date,
    endDate: Date,
    excludeNotCharge: boolean,
    filename: string
  ) {
    from(
      this.reservationReportApi.reportOccupancyExportGet(
        lightFormat(startDate, 'yyyy-MM-dd'),
        lightFormat(endDate, 'yyyy-MM-dd'),
        excludeNotCharge,
        {
          responseType: 'arraybuffer', // Pass 'arraybuffer' as responseType in options
        }
      )
    )
      .pipe(
        map(x => new Blob([x.data], { type: MediaTypes.ExcelFile })),
        dispatchForm(CommonForm.Export)
      )
      .subscribe(x => saveAs(x, filename));
  }

  downloadInvoiceSummary({ customerId, startDate, endDate, invoiceType }: InvoiceSummaryRequest) {
    from(
      this.reservationReportApi.reportInvoiceSummaryGet(
        customerId,
        startDate,
        endDate,
        invoiceType,
        {
          responseType: 'arraybuffer', // Pass 'arraybuffer' as responseType in options
        }
      )
    )
      .pipe(
        catchError((e: Error) => {
          return throwError(new Error('An error occurred while downloading the invoice summary'));
        }),
        map(x => new Blob([x.data], { type: MediaTypes.ExcelFile })),
        dispatchForm(ReportForms.InvoiceSummary)
      )
      .subscribe(x =>
        // Perform the asynchronous saveAs operation
        saveAs(
          x,
          `${EnumValueHumanizer(invoiceType, '_')}_Summary_For_${format(
            new Date(startDate),
            'M/d/yy'
          )}-${format(new Date(endDate), 'M/d/yy')}.xlsx`
        )
      );
  }

  downloadUnbilledEarnedReport({ customerId, startDate, endDate }: UnbilledEarnedReportRequest) {
    from(
      this.reservationReportApi.reportUnbilledEarnedGet(customerId, startDate, endDate, {
        responseType: 'blob',
      })
    )
      .pipe(
        map(x => new Blob([x.data], { type: MediaTypes.ExcelFile })),
        dispatchForm(ReportForms.UnbilledEarned)
      )
      .subscribe(x =>
        saveAs(
          x,
          `Unbilled_Earned_Review_${format(new Date(startDate), 'M/d/yy')}-${format(
            new Date(endDate),
            'M/d/yy'
          )}.xlsx`
        )
      );
  }

  reset() {
    this.store.reset();
  }
}

export const reportService = new ReportService(
  reportStore,
  new Properties.ReportApi(),
  new Reservations.ReportApi(),
  new Reservations.LedgerApi(),
  new PointOfSale.ReportApi()
);
